<template>
  <g
    class="room-table"
    :class="{ 'focus:outline-none': focusable }"
    :transform="`translate(${localX}, ${localY}) rotate(${rotation})`"
    :role="ariaLabel ? 'radio' : undefined"
    :tabindex="focusable ? 0 : undefined"
    :aria-checked="ariaLabel ? String(ariaChecked) : undefined"
    :aria-label="ariaLabel || undefined"
    :aria-disabled="ariaDisabled || undefined"
    ref="roomTableRef"
  >
    <!--SHAPE SELECTION-->
    <!-- Invisible Interactive Layer -->
    <rect
      v-if="shape === TableShape.SQUARE"
      v-bind="{ ...squareRect, ...propsInvisibleShape }"
      class="square-invisible"
    />
    <!-- Visible Shape -->
    <rect
      v-if="shape === TableShape.SQUARE"
      rx="0.3"
      ry="0.3"
      v-bind="{ ...squareRect, ...propsVisibleShape }"
      class="square"
    />
    <!-- Red stripe overlay for square -->
    <rect
      v-if="stripeId && shape === TableShape.SQUARE"
      rx="0.3"
      ry="0.3"
      v-bind="squareRect"
      :fill="stripe"
      pointer-events="none"
      class="square-striped"
    />
    <!-- Focus ring (square) - single external stroke to match guest focus style -->
    <rect
      v-if="isFocusRingVisible && shape === TableShape.SQUARE"
      rx="0.3"
      ry="0.3"
      v-bind="{ ...squareFocusRingRect, ...propsFocusRing }"
      class="square-focus-overlay"
    />

    <!-- Invisible Interactive Layer -->
    <circle
      v-if="shape === TableShape.CIRCLE"
      v-bind="{ ...circleCommon, ...propsInvisibleShape }"
      class="circle-invisible"
    />
    <!-- Visible Shape -->
    <circle
      v-if="shape === TableShape.CIRCLE"
      v-bind="{ ...circleCommon, ...propsVisibleShape }"
      class="circle"
    />
    <!-- Red stripe overlay for circle -->
    <circle
      v-if="stripeId && shape === TableShape.CIRCLE"
      v-bind="circleCommon"
      :fill="stripe"
      pointer-events="none"
      class="circle-striped"
    />
    <!-- Focus ring (circle) - single external stroke to match guest focus style -->
    <circle
      v-if="isFocusRingVisible && shape === TableShape.CIRCLE"
      v-bind="{ ...circleFocusRing, ...propsFocusRing }"
      class="circle-focus-overlay"
    />

    <!-- Invisible Interactive Layer -->
    <path
      v-if="shape === TableShape.HALF_CIRCLE"
      :d="halfCircleBasePath"
      v-bind="propsInvisibleShape"
      class="half-circle-invisible"
    />
    <!-- Visible Shape -->
    <path
      v-if="shape === TableShape.HALF_CIRCLE"
      :d="halfCircleBasePath"
      v-bind="propsVisibleShape"
      class="half-circle"
    />
    <!-- Red stripe overlay for half-circle -->
    <path
      v-if="stripeId && shape === TableShape.HALF_CIRCLE"
      :d="halfCircleBasePath"
      :fill="stripe"
      pointer-events="none"
      class="half-circle-striped"
    />
    <!-- Focus ring (half-circle) - single external stroke to match guest focus style -->
    <path
      v-if="isFocusRingVisible && shape === TableShape.HALF_CIRCLE"
      :d="halfCircleFocusRingPath"
      v-bind="propsFocusRing"
      class="half-circle-focus-overlay"
    />

    <!-- Invisible Interactive Layer -->
    <polygon
      v-if="shape === TableShape.TRIANGLE"
      :points="trianglePoints"
      v-bind="propsInvisibleShape"
      class="triangle-invisible"
    />
    <!-- Visible Shape -->
    <polygon
      v-if="shape === TableShape.TRIANGLE"
      :points="trianglePoints"
      v-bind="propsVisibleShape"
      class="triangle"
    />
    <!-- Red stripe overlay for triangle -->
    <polygon
      v-if="stripeId && shape === TableShape.TRIANGLE"
      :points="trianglePoints"
      :fill="stripe"
      pointer-events="none"
      class="triangle-striped"
    />
    <!-- Focus ring (triangle) - single external stroke to match guest focus style -->
    <polygon
      v-if="isFocusRingVisible && shape === TableShape.TRIANGLE"
      :points="triangleFocusRingPoints"
      v-bind="propsFocusRing"
      class="triangle-focus-overlay"
    />

    <PianoShape
      v-if="shape === TableShape.PIANO"
      :width="width"
      :height="height"
      :class="[strokeClass, fillClass, 'piano']"
    />
    <!-- Red stripe overlay for piano -->
    <PianoShape
      v-if="stripeId && shape === TableShape.PIANO"
      :width="width"
      :height="height"
      :fill="stripe"
      class="piano-striped"
      style="pointer-events: none"
    />
    <!-- Focus ring (piano: bounding rect) - single external stroke to match guest focus style -->
    <rect
      v-if="isFocusRingVisible && shape === TableShape.PIANO"
      :x="pianoFocusRingRect.x"
      :y="pianoFocusRingRect.y"
      :width="pianoFocusRingRect.width"
      :height="pianoFocusRingRect.height"
      v-bind="propsFocusRing"
      class="piano-focus-overlay"
    />

    <!--LABEL CONTENT SELECTION-->
    <g v-if="marker === 'xmark'" class="xmark">
      <line class="line-1 stroke-white" v-bind="xmarkLeftLine" :stroke-width="2 / venueScale" />
      <line class="line-2 stroke-white" v-bind="xmarkRightLine" :stroke-width="2 / venueScale" />
    </g>
    <text
      v-else-if="marker === 'checkmark'"
      x="0"
      y="0"
      dominant-baseline="middle"
      text-anchor="middle"
      class="checkmark"
      :class="[textFillClass || 'fill-white']"
      style="font-size: 1.3px"
      :transform="`rotate(${-rotation})`"
    >
      &#10003;
    </text>
    <text
      v-else-if="!truncate"
      x="0"
      y="0"
      dominant-baseline="middle"
      text-anchor="middle"
      :class="[textFillClass, 'label']"
      :style="{ fontSize: `${fontSize}px` }"
      :transform="`rotate(${-rotation})`"
    >
      <slot />
    </text>
    <foreignObject
      v-else
      :x="-width"
      :y="-height"
      :width="width * 2"
      :height="height * 2"
      :transform="`rotate(${-rotation})`"
    >
      <div
        xmlns="http://www.w3.org/1999/xhtml"
        :class="[textFillClass, 'label flex items-center justify-center text-center w-full h-full']"
        :style="{ fontSize: `${fontSize}px`, padding: `0 ${1 / aspectRatio}px` }"
      >
        <slot />
      </div>
    </foreignObject>

    <!-- ICON SELECTION -->
    <g v-if="icon" :transform="`translate(${iconX}, ${iconY})`" style="isolation: isolate">
      <circle
        :r="ICON_RADIUS"
        class="fill-surface-primary stroke-linear-strong"
        :stroke-width="ICON_RADIUS * 0.1"
      />
      <BaseIcon
        :name="icon"
        :width="ICON_RADIUS * 1.4"
        :height="ICON_RADIUS * 1.4"
        :x="ICON_RADIUS * -0.7"
        :y="ICON_RADIUS * -0.7"
        class="text-fg-primary"
      />
    </g>
  </g>
</template>

<script lang="ts" setup>
import { useDraggable } from '@vueuse/core'
import * as _ from 'lodash-es'
import { storeToRefs } from 'pinia'
import { ComputedRef, SVGAttributes, computed, inject, ref, toRefs, watch } from 'vue'

import PianoShape from '@/common/components/atoms/PianoShape.vue'
import BaseIcon from '@/common/components/atoms/BaseIcon.vue'
import { BaseIconName } from '@/common/interfaces/BaseIconInterfaces'
import { useSettingStore } from '@/stores/setting'
import { ROOM_TABLE_RELATIVE_WIDTH } from '@/utils/utils'

import { PerformanceTableRead, TableShape, TableShapeType } from '@generated/types'

export type Marker = 'checkmark' | 'xmark' | 'none'
export interface RoomTableProps {
  shape: TableShapeType
  width: number
  height: number
  x: number
  y: number
  rotation?: number
  marker?: Marker
  fillClass?: string
  strokeClass?: string
  textFillClass?: string
  draggable?: boolean
  focusable?: boolean
  isFocused?: boolean
  ariaLabel?: string
  ariaDisabled?: boolean
  ariaChecked?: boolean
  truncate?: boolean
  stripeId?: string
  fontSize?: number
  icon?: BaseIconName
}

const props = withDefaults(defineProps<RoomTableProps>(), {
  rotation: 0,
  marker: 'none',
  fillClass: 'fill-surface-table-available',
  strokeClass: 'stroke-transparent',
  textFillClass: 'fill-fg-table-available',
  draggable: false,
  focusable: false,
  isFocused: false,
  ariaLabel: '',
  ariaDisabled: false,
  ariaChecked: false,
  truncate: false,
  stripeId: undefined,
  fontSize: 1.3,
  icon: undefined,
})
const {
  width,
  height,
  shape,
  draggable,
  strokeClass,
  fillClass,
  focusable,
  isFocused,
  ariaLabel,
  ariaDisabled,
  ariaChecked,
} = toRefs(props)
const { ratio: aspectRatio } = storeToRefs(useSettingStore())
const stripeId = computed<string | undefined>(() =>
  props.stripeId ? _.kebabCase(props.stripeId) : undefined,
)
const stripe = computed<string | undefined>(() =>
  stripeId.value ? `url(#${stripeId.value})` : undefined,
)

const trianglePoints = computed<string>(function () {
  return `${-width.value * 1.5},${height.value * 0.5} ${width.value * 0.5},${height.value * 0.5} ${
    width.value * 0.5
  },${-height.value * 1.5} ${-width.value * 1.5},${height.value * 0.5}`
})
const triangleFocusRingPoints = computed<string>(function () {
  const o = focusRingOffset.value
  const w = width.value
  const h = height.value
  const cx = ((-1.5 + 0.5 + 0.5) * w) / 3
  const cy = ((0.5 + 0.5 - 1.5) * h) / 3
  // Scale uniformly around the centroid so the focus ring expands outward consistently
  // regardless of triangle width/height proportions.
  const rA2 = (16 * w * w + 4 * h * h) / 9 // distance^2 from centroid to vertex A
  const rC2 = (4 * w * w + 16 * h * h) / 9 // distance^2 from centroid to vertex C
  const maxDist = Math.sqrt(Math.max(rA2, rC2))
  const scale = maxDist > 0 ? 1 + o / maxDist : 1
  const pts = [
    [-1.5 * w, 0.5 * h],
    [0.5 * w, 0.5 * h],
    [0.5 * w, -1.5 * h],
    [-1.5 * w, 0.5 * h],
  ]
  return pts
    .map(([px, py]) => [cx + (px - cx) * scale, cy + (py - cy) * scale])
    .map(([x, y]) => `${x},${y}`)
    .join(' ')
})
const halfCircleFocusRingPath = computed<string>(function () {
  const w = width.value
  const hw = w / 2
  const o = focusRingOffset.value
  const r = w + o
  return `M-${r},-${hw} a${r},${r} 0 0,0 ${r * 2},0`
})
const halfCircleBasePath = computed<string>(function () {
  const w = width.value
  const hw = w / 2
  return `M-${w},-${hw} a${w},${w} 0 0,0 ${w * 2},0`
})
const xmarkMargin = computed<number>(function () {
  switch (shape.value) {
    case TableShape.CIRCLE:
      return width.value * 0.4
    case TableShape.HALF_CIRCLE:
      return width.value * 0.7
    default:
      return 0.6
  }
})
const xmarkLeftLine = computed<SVGAttributes>(function () {
  if (shape.value === TableShape.CIRCLE)
    return {
      x1: -width.value + xmarkMargin.value,
      y1: -width.value + xmarkMargin.value,
      x2: width.value - xmarkMargin.value,
      y2: width.value - xmarkMargin.value,
    }

  return {
    x1: -width.value + xmarkMargin.value,
    y1: -height.value + xmarkMargin.value,
    x2: width.value - xmarkMargin.value,
    y2: height.value - xmarkMargin.value,
  }
})
const xmarkRightLine = computed<SVGAttributes>(function () {
  if (shape.value === TableShape.CIRCLE)
    return {
      x1: -width.value + xmarkMargin.value,
      y1: width.value - xmarkMargin.value,
      x2: width.value - xmarkMargin.value,
      y2: -width.value + xmarkMargin.value,
    }

  return {
    x1: -width.value + xmarkMargin.value,
    y1: height.value - xmarkMargin.value,
    x2: width.value - xmarkMargin.value,
    y2: -height.value + xmarkMargin.value,
  }
})

// ICON POSITIONING
const ICON_RADIUS = 0.9
const iconX = computed<number>(function () {
  switch (shape.value) {
    case TableShape.CIRCLE:
      // For circle: position just outside circle at top-right
      return width.value * Math.cos(-Math.PI / 4) + ICON_RADIUS * 0.5
    case TableShape.HALF_CIRCLE:
      // For half-circle: right edge at top
      return width.value
    case TableShape.TRIANGLE:
      // For triangle: top-right corner is at (width * 0.5, -height * 1.5)
      return width.value * 0.5
    case TableShape.PIANO:
      // For piano: right edge
      return width.value
    default:
      // For square and rectangle
      return width.value
  }
})
const iconY = computed<number>(function () {
  switch (shape.value) {
    case TableShape.CIRCLE:
      // For circle: position just outside circle at top-right
      return width.value * Math.sin(-Math.PI / 4) - ICON_RADIUS * 0.5
    case TableShape.HALF_CIRCLE:
      // For half-circle: top of the arc (flat edge at -width/2)
      return -width.value / 2
    case TableShape.TRIANGLE:
      // For triangle: top-right corner is at (width * 0.5, -height * 1.5)
      return -height.value * 1.5
    case TableShape.PIANO:
      // For piano: top edge
      return -height.value
    default:
      // For square and rectangle
      return -height.value
  }
})

// D R A G G I N G    F U N C T I O N A L I T Y
const emit = defineEmits<{
  // eslint-disable-next-line no-unused-vars
  (event: 'update:position', values: Pick<PerformanceTableRead, 'x' | 'y'>): void
}>()
const localX = ref(props.x)
const localY = ref(props.y)
watch([() => props.x, () => props.y], ([x, y]) => {
  localX.value = x
  localY.value = y
})
let mouseToCenterOffset: { xDistance: number; yDistance: number } = { xDistance: 0, yDistance: 0 }
const roomTableRef = ref<SVGGraphicsElement | null>(null)
const svgElementRef = inject('svgElementRef') as ComputedRef<SVGElement | null>
const venueRoomPaddingX = inject('paddingX') as ComputedRef<number>
const venueScale = inject('venueScale') as ComputedRef<number>
const svgRelativeWidth = inject('svgRelativeWidth') as ComputedRef<number> | undefined
const svgRelativeHeight = inject('svgRelativeHeight') as ComputedRef<number> | undefined
useDraggable(roomTableRef, {
  draggingElement: svgElementRef,
  containerElement: svgElementRef,
  onStart(_, { offsetX, offsetY }) {
    if (!draggable.value) return
    const { x, y } = getMouseRelativePosition(offsetX, offsetY)
    mouseToCenterOffset = { xDistance: x - localX.value, yDistance: y - localY.value }
  },
  onMove(_, { offsetX, offsetY }) {
    if (!draggable.value) return
    const { x, y } = getTableRelativePosition(offsetX, offsetY)
    localX.value = x
    localY.value = y
  },
  onEnd(_, { offsetX, offsetY }) {
    if (!draggable.value) return
    const { x, y } = getTableRelativePosition(offsetX, offsetY)
    emit('update:position', { x: Math.round(x), y: Math.round(y) })
  },
})
function getMouseRelativePosition(offsetX: number, offsetY: number): { x: number; y: number } {
  return {
    x: (offsetX - venueRoomPaddingX.value) / venueScale.value,
    y: offsetY / venueScale.value,
  }
}
function getTableRelativePosition(offsetX: number, offsetY: number): { x: number; y: number } {
  const currentSvgRelativeWidth = svgRelativeWidth?.value ?? ROOM_TABLE_RELATIVE_WIDTH
  const currentSvgRelativeHeight =
    svgRelativeHeight?.value ?? ROOM_TABLE_RELATIVE_WIDTH / aspectRatio.value
  const { x: relativeMouseX, y: relativeMouseY } = getMouseRelativePosition(offsetX, offsetY)
  return {
    x: _.clamp(relativeMouseX - mouseToCenterOffset.xDistance, 0, currentSvgRelativeWidth),
    y: _.clamp(relativeMouseY - mouseToCenterOffset.yDistance, 0, currentSvgRelativeHeight),
  }
}

const STROKE_WIDTH = 2
const propsVisibleShape = computed<SVGAttributes>(() => ({
  'stroke-width': STROKE_WIDTH / venueScale.value,
  'stroke-linejoin': 'round',
  class: [strokeClass.value, fillClass.value],
}))

const STROKE_TARGET_AREA = 6
const propsInvisibleShape = computed<SVGAttributes>(() => ({
  'stroke-width': STROKE_TARGET_AREA / venueScale.value,
  'stroke-linejoin': 'round',
  class: 'stroke-transparent',
  fill: 'transparent',
}))

const FOCUS_GUTTER_PX = 3
const FOCUS_RING_PX = 3
const isFocusRingVisible = computed<boolean>(() => focusable.value && isFocused.value)
const focusRingOffset = computed<number>(
  () => (FOCUS_GUTTER_PX + FOCUS_RING_PX / 2) / venueScale.value,
)

const squareRect = computed(() => ({
  x: -width.value,
  y: -height.value,
  width: width.value * 2,
  height: height.value * 2,
}))
const squareFocusRingRect = computed(() => ({
  x: -width.value - focusRingOffset.value,
  y: -height.value - focusRingOffset.value,
  width: width.value * 2 + focusRingOffset.value * 2,
  height: height.value * 2 + focusRingOffset.value * 2,
}))

const circleCommon = computed(() => ({ cx: 0, cy: 0, r: width.value }))
const circleFocusRing = computed(() => ({
  cx: 0,
  cy: 0,
  r: width.value + focusRingOffset.value,
}))

// Keep geometry in sync with `PianoShape.vue` (which uses width*8.1/height*6.8 viewBox mapping).
const PIANO_SVG_WIDTH_RATIO = 8.1
const PIANO_SVG_HEIGHT_RATIO = 6.8
const pianoFocusRingRect = computed<{ x: number; y: number; width: number; height: number }>(() => {
  const halfWidth = PIANO_SVG_WIDTH_RATIO / 2
  const halfHeight = PIANO_SVG_HEIGHT_RATIO / 2
  return {
    x: -width.value * halfWidth - focusRingOffset.value,
    y: -height.value * halfHeight - focusRingOffset.value,
    width: width.value * PIANO_SVG_WIDTH_RATIO + focusRingOffset.value * 2,
    height: height.value * PIANO_SVG_HEIGHT_RATIO + focusRingOffset.value * 2,
  }
})

const propsFocusRing = computed<SVGAttributes>(() => ({
  fill: 'none',
  'stroke-width': FOCUS_RING_PX / venueScale.value,
  'stroke-linejoin': 'round',
  class: 'stroke-fg-g-primary pointer-events-none',
}))
</script>
